import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';
import {
  ActionDrawer,
  CurrentSignal,
  NonPlatformCustomerData,
  NonPlatformTeamMemberData,
  SelectedMessageAction,
} from './model';
import { ChatActionType, MessageEnvelope, SignalData } from '@zonofi/common';

export const MESSAGE_FEATURE_KEY = 'msgData';

export interface MsgState {
  messages: { [channel: string]: MessageEnvelope[] };
  msgPagination: { [channel: string]: boolean };
  signals: { [conversationId: string]: SignalData };
  currentSignal: CurrentSignal;
  signalAction: { [signalId: string]: string[] };
  chatActionDrawer: ActionDrawer;
  scrollToBottom: boolean;
  selectedMessage?: SelectedMessageAction;
  showLoader: boolean;
  nonPlatformCustomerData: NonPlatformCustomerData;
  nonPlatformTeamMemberData: NonPlatformTeamMemberData;
  shortcutsKeyBoard: boolean;
}

export const initialMsgState: MsgState = {
  messages: {},
  msgPagination: {},
  signals: {},
  currentSignal: {
    conversationId: '',
    startTime: undefined,
    endTime: undefined,
  },
  signalAction: {},
  chatActionDrawer: {
    open: false,
    type: ChatActionType.NONE,
    suggestions: [],
    entityId: undefined,
    customerId: '',
    isRedirectFromZoAI: false,
  },
  scrollToBottom: false,
  showLoader: false,
  nonPlatformCustomerData: {},
  nonPlatformTeamMemberData: {},
  shortcutsKeyBoard: false,
};

export const msgSlice = createSlice({
  name: MESSAGE_FEATURE_KEY,
  initialState: initialMsgState,
  reducers: {
    updateMessagesByChannelId(
      state,
      action: PayloadAction<{ channelId: string; messages: MessageEnvelope[] }>
    ) {
      const { channelId, messages } = action.payload;
      const existingMessages = state.messages[channelId] ?? [];
      const mergedMessages = new Map<string, MessageEnvelope>();

      const upsertMergedMessage = (message: MessageEnvelope) => {
        const messageKey =
          message.message?._id ??
          message.timetoken?.toString() ??
          String(message.displayTimetoken ?? '');
        const previousMessage = mergedMessages.get(messageKey);

        if (!previousMessage) {
          mergedMessages.set(messageKey, message);
          return;
        }

        mergedMessages.set(messageKey, {
          ...previousMessage,
          ...message,
          message: {
            ...previousMessage.message,
            ...message.message,
          },
        });
      };

      existingMessages.forEach(upsertMergedMessage);
      messages.forEach(upsertMergedMessage);

      state.messages[channelId] = Array.from(mergedMessages.values()).sort(
        (a, b) =>
          Number(a.displayTimetoken ?? a.timetoken) -
          Number(b.displayTimetoken ?? b.timetoken)
      );
    },
    updateChannelPagination(
      state,
      action: PayloadAction<{ channelId: string; pagination: boolean }>
    ) {
      const { channelId, pagination } = action.payload;
      state.msgPagination[channelId] = pagination;
    },
    upsertMessage(state, action: PayloadAction<Partial<MessageEnvelope>>) {
      const message = action.payload;
      if (!message?.channel || !message?.message) return;

      const { channel, message: messagePayload } = message;

      if (!messagePayload._id) return;

      const enrichedMessage = {
        ...message,
        displayTimetoken: messagePayload.eventTimestamp ?? message.timetoken,
      };

      const existingChannel = state.messages[channel] ?? [];

      const index = existingChannel.findIndex(
        ({ message: msg }) => msg._id === messagePayload._id
      );

      if (index !== -1) {
        const old = existingChannel[index];

        existingChannel[index] = {
          ...old,
          ...enrichedMessage,
          message: {
            ...old.message,
            ...enrichedMessage.message,
          },
        };
      } else {
        existingChannel.push(enrichedMessage as MessageEnvelope);
      }

      existingChannel.sort((a, b) =>
        String(a.timetoken).localeCompare(String(b.timetoken))
      );

      state.messages[channel] = existingChannel;
    },
    clearAllMessageData(state) {
      state.messages = {};
      state.msgPagination = {};
    },
    updateChatActionDrawer(state, action: PayloadAction<ActionDrawer>) {
      state.chatActionDrawer = { ...action?.payload };
    },
    updateChatActionCustomerId(state, action: PayloadAction<string>) {
      state.chatActionDrawer.customerId = action.payload;
    },
    closeChatActionDrawer(state) {
      state.chatActionDrawer = initialMsgState.chatActionDrawer;
    },
    updateScrollToBottom(state, action: PayloadAction<boolean>) {
      state.scrollToBottom = action.payload;
    },
    updateShortCut(state, action: PayloadAction<boolean>) {
      state.shortcutsKeyBoard = action.payload;
    },
    updateSelectedMessage(state, action: PayloadAction<SelectedMessageAction>) {
      state.selectedMessage = action.payload;
    },
    updateShowLoader(state, action: PayloadAction<boolean>) {
      state.showLoader = action.payload;
    },
    updateNonPlatformCustomerData(
      state,
      action: PayloadAction<NonPlatformCustomerData>
    ) {
      state.nonPlatformCustomerData = action.payload;
    },
    updateNonPlatformTeamMemberData(
      state,
      action: PayloadAction<NonPlatformTeamMemberData>
    ) {
      state.nonPlatformTeamMemberData = action.payload;
    },
    updateSignals(
      state,
      action: PayloadAction<{ [conversationId: string]: SignalData }>
    ) {
      state.signals = { ...state.signals, ...action.payload };
    },
    updateCurrentSignal(state, action: PayloadAction<CurrentSignal>) {
      state.currentSignal = action.payload;
    },
    updateSignalAction(
      state,
      action: PayloadAction<{ [signalId: string]: string[] }>
    ) {
      state.signalAction = { ...state.signalAction, ...action.payload };
    },
  },
});

export const {
  updateMessagesByChannelId,
  updateChannelPagination,
  upsertMessage,
  clearAllMessageData,
  updateChatActionDrawer,
  updateChatActionCustomerId,
  updateScrollToBottom,
  updateShortCut,
  closeChatActionDrawer,
  updateSelectedMessage,
  updateShowLoader,
  updateNonPlatformCustomerData,
  updateNonPlatformTeamMemberData,
  updateSignals,
  updateCurrentSignal,
  updateSignalAction,
} = msgSlice.actions;

export const getMsgSliceState = (rootState: RootState): MsgState =>
  rootState[MESSAGE_FEATURE_KEY];

export const getChannelMessages = (channelId: string) =>
  createSelector(
    getMsgSliceState,
    (state) => state.messages?.[channelId] || []
  );

export const getAllMessages = createSelector(
  getMsgSliceState,
  (state) => state.messages
);

export const getCurrentChannelPagination = (channelId: string) =>
  createSelector(
    getMsgSliceState,
    (state) => state.msgPagination?.[channelId] || false
  );

export const getChatActionDrawer = createSelector(
  getMsgSliceState,
  (state) => state.chatActionDrawer
);

export const getIsScrollToBottom = createSelector(
  getMsgSliceState,
  (state) => state.scrollToBottom
);

export const getSelectedMessage = createSelector(
  getMsgSliceState,
  (state) => state.selectedMessage
);

export const getShowLoader = createSelector(
  getMsgSliceState,
  (state) => state.showLoader
);

export const getNonPlatformCustomerData = createSelector(
  getMsgSliceState,
  (state) => state.nonPlatformCustomerData
);

export const getNonPlatformTeamMemberData = createSelector(
  getMsgSliceState,
  (state) => state.nonPlatformTeamMemberData
);

export const getShortCutsKeyBoards = createSelector(
  getMsgSliceState,
  (state) => state.shortcutsKeyBoard
);

export const getSignals = createSelector(
  getMsgSliceState,
  (state) => state.signals
);

export const getCurrentSignal = createSelector(
  getMsgSliceState,
  (state) => state.currentSignal
);

export const getSignalAction = createSelector(
  getMsgSliceState,
  (state) => state.signalAction
);
